import './NewPasswordFields.css';
import { ReactNode, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
    buildPasswordRequirements,
    buildPasswordSchema,
    PasswordRequirement,
    usePasswordPolicy
} from '../../hooks/usePasswordPolicy';

export interface NewPasswordState {
    password: string;
    setPassword: (value: string) => void;
    confirm: string;
    setConfirm: (value: string) => void;
    requirements: PasswordRequirement[];
    /** Пароль удовлетворяет политике и совпадает с подтверждением. */
    isValid: boolean;
    reset: () => void;
}

/**
 * Состояние пары «новый пароль + подтверждение». Политика приходит с сервера,
 * подтверждение сверяется только здесь — на сервер уходит один пароль.
 * username нужен для правила notUsername; без него правило не проверяется.
 */
export function useNewPassword(username?: string): NewPasswordState {
    const policy = usePasswordPolicy();
    const [password, setPassword] = useState<string>('');
    const [confirm, setConfirm] = useState<string>('');

    const requirements = useMemo(() => buildPasswordRequirements(policy, username), [policy, username]);
    const schema = useMemo(() => buildPasswordSchema(requirements), [requirements]);

    const isValid = schema.safeParse(password).success && confirm === password;

    function reset(): void {
        setPassword('');
        setConfirm('');
    }

    return { password, setPassword, confirm, setConfirm, requirements, isValid, reset };
}

interface NewPasswordFieldsProps {
    state: NewPasswordState;
    disabled?: boolean;
    groupClassName?: string;
}

/** Поле нового пароля с живым чек-листом политики и поле подтверждения. */
function NewPasswordFields({ state, disabled, groupClassName = 'login-form' }: NewPasswordFieldsProps): ReactNode {
    const { t } = useTranslation(['password']);

    const isMismatch = state.confirm.length > 0 && state.confirm !== state.password;

    return (
        <>
            <Form.Group className={groupClassName} controlId="formNewPassword">
                <Form.Label>{t('new_password')}</Form.Label>
                <Form.Control
                    type="password"
                    autoComplete="new-password"
                    placeholder={t('new_password_placeholder')}
                    value={state.password}
                    disabled={disabled}
                    onChange={e => state.setPassword(e.currentTarget.value)}
                />
                <ul className="password-rules" aria-live="polite">
                    {state.requirements.map((requirement) => {
                        const isMet = requirement.test(state.password);
                        return (
                            <li key={requirement.key} className={isMet ? 'password-rule-met' : undefined}>
                                {t(requirement.key, requirement.params)}
                            </li>
                        );
                    })}
                </ul>
            </Form.Group>

            <Form.Group className={groupClassName} controlId="formConfirmPassword">
                <Form.Label>{t('confirm_password')}</Form.Label>
                <Form.Control
                    type="password"
                    autoComplete="new-password"
                    placeholder={t('confirm_password_placeholder')}
                    value={state.confirm}
                    disabled={disabled}
                    isInvalid={isMismatch}
                    onChange={e => state.setConfirm(e.currentTarget.value)}
                />
                <Form.Control.Feedback type="invalid">{t('passwords_dont_match')}</Form.Control.Feedback>
            </Form.Group>
        </>
    );
}

/** Ошибки сервера: список из 400 `{ errors }` или одно общее сообщение. */
export function PasswordFormErrors({ errors }: { errors: string[] }): ReactNode {
    if (errors.length === 0) {
        return null;
    }

    if (errors.length === 1) {
        return <div className="password-form-error">{errors[0]}</div>;
    }

    return (
        <div className="password-form-error">
            <ul>
                {errors.map((error) => <li key={error}>{error}</li>)}
            </ul>
        </div>
    );
}

export default NewPasswordFields;
